
import Cache, {CACHE_TIME_4_HOUR, CACHE_TIME_15_MIN} from 'src/Cache/Cache';
import axios from 'axios';
import Graphql from 'src/Api/Graphql';
import Drupal from 'src/Api/Drupal';
import {ETF, FUND} from 'src/Api/MorningStar';
import * as Sentry from '@sentry/nextjs';

export const MARKET_LSE = 'LSE';

const MARKET_TSE = 'TSE';
const MARKET_XETRA = 'XETRA';
const MARKET_SWX = 'SWX';
const MARKET_NASDAQ = 'NASDAQ';
const MARKET_EURONEXT = 'EURONEXT';
const MARKET_MTA = 'MTA';
const MARKET_XMAD = 'XMAD';
const MARKET_NYSE = 'NYSE';
const MARKET_SIBE = 'SIBE';
const MARKET_AMEX = 'AMEX';
const MARKET_XSTO = 'XSTO';
const MARKET_TSX = 'TSX';
const MARKET_XNAS = 'XNAS';
const MARKET_XDJI = 'XDJI';

const FIGARO_MARKET_EXL = 'EXL';
const FIGARO_MARKET_NCT = 'NCT';
const FIGARO_MARKET_EDF = 'EDF';
const FIGARO_MARKET_ESE = 'ESE';
const FIGARO_MARKET_NAO = 'NAO';
const FIGARO_MARKET_EFC = 'EFC';
const FIGARO_MARKET_EIC = 'EIC';
const FIGARO_MARKET_NAY = 'NAY';
const FIGARO_MARKET_EEC = 'EEC';
const FIGARO_MARKET_NAA = 'NAA';
const FIGARO_MARKET_SSS = 'SSS';

const MORNINGSTAR_MARKET_TO_FIGARO_MARKET = {
    [MARKET_LSE]:  FIGARO_MARKET_EXL,
    [MARKET_TSE]: FIGARO_MARKET_NCT,
    [MARKET_XETRA]: FIGARO_MARKET_EDF,
    [MARKET_SWX]: FIGARO_MARKET_ESE,
    [MARKET_NASDAQ]: FIGARO_MARKET_NAO,
    [MARKET_EURONEXT]: FIGARO_MARKET_EFC,
    [MARKET_MTA]: FIGARO_MARKET_EIC,
    [MARKET_NYSE]: FIGARO_MARKET_NAY,
    [MARKET_SIBE]: FIGARO_MARKET_EEC,
    [MARKET_XMAD]: FIGARO_MARKET_EEC,
    [MARKET_AMEX]: FIGARO_MARKET_NAA,
    [MARKET_XSTO]: FIGARO_MARKET_SSS,
    [MARKET_TSX]: FIGARO_MARKET_NCT,
    [MARKET_XNAS]: FIGARO_MARKET_NAO,
    [MARKET_XDJI]: FIGARO_MARKET_NAO
};

export const COMPLEX_INSTRUMENT = 1;

export const INSTRUMENT_TYPE_SHARE = 1;
export const INSTRUMENT_TYPE_FUND = 2;
export const INSTRUMENT_TYPE_ETF = 3;
export const INSTRUMENT_TYPE_INVESTMENT_TRUST = 4;

export const CONFIGURED_SERVICE_WATCHLISTS = 40;
export const CONFIGURED_SERVICE_ETF_SCREENER_TAB = 50;
export const CONFIGURED_SERVICE_SHARES_SCREENER_TAB = 51;
export const CONFIGURED_SERVICE_TRUSTS_SCREENER_TAB = 53;
export const CONFIGURED_SERVICE_MARKET_SENTIMENT_CHART = 60;
export const CONFIGURED_SERVICE_MARKET_SECTOR_TAB = 62;
export const CONFIGURED_SERVICE_MARKET_TECHMARK_TAB = 65;
export const CONFIGURED_SERVICE_STOCK_SUMMARIES = 94;
export const CONFIGURED_SERVICE_REBRANDED_HEADER_FOOTER = 132;
export const YOUINVEST_DEVICE_TOKEN = 'e26a3e21-06d0-5078-902b-35b08724hd18';

export default class YouInvest {

    static getTradingRequestHeaders() {
        // currently the process.env.NEXT_PUBLIC_YOUINVEST_DEVICE_TOKEN env variable is not working with public
        return {
            'Authorization': 'YouInvestDeviceToken token=' + YOUINVEST_DEVICE_TOKEN,
            'X-Trading-Brand': 'trading'
        };
    }

    static getConfiguredServiceAllowed (serviceId, settings) {
        if (!settings || !settings.configuredServices) {
            return false
        }
        
        const {configuredServices} = settings;
        
        for (let s in configuredServices) {
            if (configuredServices[s].serviceId == serviceId) {
                return true;
            }
        }

        return false;
    }

    static async getNavigation() {
        const instrumentData = {};

        try {
            instrumentData.nav = await Drupal.getDrupalNavigation();
        } catch(e) {
            Sentry.captureException(e);
        }

        return instrumentData;
    }

    static async getBasicPageData(marketCode, newNav = false) {

        const rsp = await Graphql.getQuote(marketCode, '[IT_SELECT_LIST, FAVORITE_FUNDS]');
    
        // Now enrich the instrument with trading data so we know if tradable in the YI universe
        const instrumentData = await YouInvest.enrichWithTradingData(rsp);

        try {
            instrumentData.settings = await YouInvest.getSettings();
        } catch (e) {
            Sentry.captureException(e);
        }

        try { 
            instrumentData.marketOpen = await YouInvest.getMarketOpen(rsp.quote);
        } catch (e) {
            Sentry.captureException(e);
        }

        try {
            instrumentData.nav = await Drupal.getDrupalNavigation(newNav);
        } catch(e) {
            Sentry.captureException(e);
        }

        /*
        try {
            const getSecuritySummaryResponse = await Graphql.getSecuritySummary(marketCode);
            instrumentData.securitySummary = getSecuritySummaryResponse.securitySummary;
        } catch(e) {
            Sentry.captureException(e);
        } */
        return instrumentData;
    }

    static async getMarketOpen(quote) {

        if (quote.__typename === FUND) {
            return null; 
        }

        const {exchangeCode} = quote;

        if (MORNINGSTAR_MARKET_TO_FIGARO_MARKET[exchangeCode]) {

            const cache = new Cache; 
            const key = `market_open_${exchangeCode}`;
            const marketOpen = cache.getItem(key);

            if (marketOpen) {
                return marketOpen;
            }

            const mappedMarket = MORNINGSTAR_MARKET_TO_FIGARO_MARKET[exchangeCode];
            const response = await YouInvest.fetch(`/api/general/market?code=${mappedMarket}`);

            if(response.data && response.data.exchangeCode) {
                cache.setItem(key, response.data, CACHE_TIME_4_HOUR);
                return response.data;
            }
        } 
            
        return null;
    }
    
    static async fetch(action) {        
        const response = await axios({
            method: 'GET',
            url: `${process.env.TRADING_ENDPOINT}${action}`,
            headers: YouInvest.getTradingRequestHeaders()
        })
        return response.data; 
    }

    static async getFavouriteFunds() {
        try {
            const cache = new Cache; 
            const key = `favourite_funds`;
            let favourites = cache.getItem(key);

            if (favourites) {
                return favourites;
            }
            
            const response = await Graphql.getFavourites();

            if (response) {  
                const favouriteFunds = response; 
                cache.setItem(key, favouriteFunds, CACHE_TIME_4_HOUR);
                return favouriteFunds;   
            }
		} catch (e) {

		}

		return [];
    }

    static async enrichWithTradingData(instrumentData) {

		try {
            const {marketCode} = instrumentData.quote;

            const cache = new Cache; 
            const key = `trading_instrument_${marketCode}`;
            let instrument = cache.getItem(key);

            if (instrument) {
                instrumentData.instrument = instrument;
                return instrumentData;
            }
            
            const response = await YouInvest.fetch(`/api/securities/instrument?marketCode=${marketCode}`);

            if (response.data && response.data) {  
                const tradingInstrument = response.data; 
                cache.setItem(key, tradingInstrument, CACHE_TIME_4_HOUR);
                instrumentData.instrument = tradingInstrument;   
            }

            if (instrumentData.quote.figaroData) {
                instrumentData.instrument.figaroData = instrumentData.quote.figaroData;
            }
		} catch (e) {

		}

		return instrumentData;
    }

    static async getSettings() {
		try {
            const cache = new Cache; 
            const key = `trading_settings`;
            let settings = cache.getItem(key);

            if (settings) {
                return settings;
            }

            const response = await YouInvest.fetch(`/api/settings/init`);

            if(response.data) {
                response.data.baseUrl = process.env.YOUINVEST_BASE_URL;
                cache.setItem(key, response.data, CACHE_TIME_15_MIN);
                return response.data;
            }
		} catch (e) {
            Sentry.captureException(e);
		}

		return null;
    }

    static async getLatestMagazine() {
		try {
            const cache = new Cache; 
            const key = `trading_magazine`;
            let magazine = cache.getItem(key);

            if (magazine) {
                return magazine;
            }

            const response = await YouInvest.fetch(`/api/general/latest-magazine`);

            if(response.data) {
                cache.setItem(key, response.data, CACHE_TIME_15_MIN);
                return response.data;
            }
		} catch (e) {
            Sentry.captureException(e);
		}

		return null;
    }

    static internationalExchangeCodeMapping = (exchangeCode) => {
        const internationalExchangeCode = {
            'OSTO': 'XSTO', // Stockholm Stock Exchange
            'MIL': 'MTA', // Borsa Italiana
            'TSX': 'TSE', // Toronto Stock Exchange
            'XSWX': 'SWX', // SIX Swiss Exchange
            'XETR': 'XETRA', // Xetra
            'XAMS': 'EURONEXT', // Euronext Paris
            'XPAR': 'EURONEXT' // Euronext Amsterdam
        };
    
        if (exchangeCode in internationalExchangeCode) {
            return internationalExchangeCode[exchangeCode];
        }
    
         return exchangeCode;
    }
}
